import React, { useMemo, useState } from 'react';
import { Box, Button, InputBase, Switch, Typography, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AiAgentService from '../../services/aiAgent.service';
import { cueLabelSx, getTokens, metaSx } from './aiAgentTheme';

/**
 * Jedno miesto, odkiaľ sa spúšťajú všetky hovory asistenta.
 *
 * Predtým boli dve nesúvisiace cesty: zoznam mien s tlačidlom (appka) a
 * samostatná karta s formulárom (telefón). Rovnaká úloha „nech AI niekomu
 * zavolá" tak vyzerala dvakrát inak. Teraz je rozdiel jediná voľba hore a
 * zvyšok formulára je spoločný.
 */

/** Rovnaká normalizácia ako na serveri – aby sme vedeli tlačidlo zašednúť. */
const normalizePhone = (raw) => {
    const trimmed = String(raw || '').replace(/[\s\-()/]/g, '');
    if (!trimmed) return null;
    if (/^\+[1-9]\d{6,14}$/.test(trimmed)) return trimmed;
    if (/^00[1-9]\d{6,14}$/.test(trimmed)) return `+${trimmed.slice(2)}`;
    if (/^0\d{8,9}$/.test(trimmed)) return `+421${trimmed.slice(1)}`;
    return null;
};

const MODES = [
    { key: 'app', label: 'Do aplikácie', hint: 'zazvoní v appke, asistent uvidí zdieľanú obrazovku' },
    { key: 'phone', label: 'Na telefón', hint: 'skutočný telefonát, volaný nemusí mať appku' },
];

const AiCallComposer = ({ employees, disabled, onCalled, onError }) => {
    const theme = useTheme();
    const tokens = getTokens(theme.palette.mode === 'dark', theme.palette.primary.main);

    const [mode, setMode] = useState('app');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [phone, setPhone] = useState('');
    const [task, setTask] = useState('');
    const [record, setRecord] = useState(false);
    const [calling, setCalling] = useState(false);

    const normalized = useMemo(() => normalizePhone(phone), [phone]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return employees;
        return employees.filter((e) => `${e.name || ''} ${e.email || ''}`.toLowerCase().includes(q));
    }, [employees, search]);

    const ready = mode === 'app' ? !!selected : !!normalized;
    const activeMode = MODES.find((m) => m.key === mode);

    const handleCall = () => {
        if (!ready || calling) return;
        setCalling(true);
        const request =
            mode === 'app'
                ? AiAgentService.callEmployee(selected.id, { task: task.trim(), record })
                : AiAgentService.callPhone(normalized, { task: task.trim(), record });
        request
            .then((res) => {
                setTask('');
                onCalled?.(mode, res.data, selected);
            })
            .catch((err) => onError?.(err?.response?.data?.message || 'Hovor sa nepodarilo spustiť'))
            .finally(() => setCalling(false));
    };

    const fieldSx = {
        fontSize: 14.5,
        lineHeight: 1.55,
        color: tokens.ink,
        border: `1px solid ${tokens.hairline}`,
        px: 1.5,
        py: 1,
        '& input::placeholder, & textarea::placeholder': {
            color: tokens.inkMuted,
            opacity: 1,
            fontStyle: 'italic',
        },
        '&.Mui-focused': { borderColor: tokens.accent },
    };

    return (
        <Box sx={{ border: `1px solid ${tokens.hairline}`, borderLeft: `2px solid ${tokens.spine}` }}>
            {/* Voľba linky – jediné, čím sa oba druhy hovoru líšia */}
            <Box sx={{ display: 'flex', borderBottom: `1px solid ${tokens.hairline}` }}>
                {MODES.map((m) => {
                    const on = m.key === mode;
                    return (
                        <Box
                            key={m.key}
                            component="button"
                            type="button"
                            onClick={() => setMode(m.key)}
                            sx={{
                                flex: 1,
                                appearance: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                textAlign: 'left',
                                px: 2,
                                py: 1.5,
                                border: 'none',
                                borderBottom: `2px solid ${on ? tokens.accent : 'transparent'}`,
                                bgcolor: on ? 'transparent' : tokens.surfaceSunken,
                                '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: -2 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14.5,
                                    fontWeight: on ? 600 : 400,
                                    color: on ? tokens.ink : tokens.inkMuted,
                                }}
                            >
                                {m.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ ...metaSx(tokens), mb: 2.5, display: 'block' }}>{activeMode.hint}</Typography>

                <Typography sx={{ ...cueLabelSx(tokens), mb: 0.75 }}>Komu</Typography>

                {mode === 'app' ? (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                border: `1px solid ${tokens.hairline}`,
                                px: 1.5,
                                py: 0.5,
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 18, color: tokens.inkMuted }} />
                            <InputBase
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Hľadať meno alebo e-mail"
                                sx={{ fontSize: 14, color: tokens.ink }}
                            />
                            <Typography sx={metaSx(tokens)}>{filtered.length}</Typography>
                        </Box>

                        <Box sx={{ maxHeight: 208, overflowY: 'auto', mb: 2.5 }}>
                            {filtered.map((employee) => {
                                const on = selected?.id === employee.id;
                                return (
                                    <Box
                                        key={employee.id}
                                        onClick={() => setSelected(employee)}
                                        sx={{
                                            borderBottom: `1px solid ${tokens.hairline}`,
                                            borderLeft: `2px solid ${on ? tokens.accent : 'transparent'}`,
                                            bgcolor: on ? tokens.accentSoft : 'transparent',
                                            cursor: 'pointer',
                                            pl: 1.25,
                                            pr: 1,
                                            py: 1,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 14.5, color: tokens.ink }} noWrap>
                                            {employee.name}
                                        </Typography>
                                        <Typography sx={metaSx(tokens)} noWrap>
                                            {[employee.email, employee.employer_email].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            {filtered.length === 0 && (
                                <Typography sx={{ color: tokens.inkMuted, fontSize: 14, py: 2 }}>
                                    {employees.length === 0
                                        ? 'Zatiaľ tu nie sú žiadni ľudia.'
                                        : 'Hľadaniu nikto nezodpovedá.'}
                                </Typography>
                            )}
                        </Box>
                    </>
                ) : (
                    <>
                        <InputBase
                            fullWidth
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="0905 123 456"
                            sx={fieldSx}
                        />
                        <Typography sx={{ ...metaSx(tokens), display: 'block', mt: 0.5, mb: 2.5 }}>
                            {phone && !normalized
                                ? 'Tomuto číslu nerozumiem'
                                : normalized
                                  ? `Vytočíme ${normalized}`
                                  : 'Stačí slovenské číslo s nulou alebo tvar +421…'}
                        </Typography>
                    </>
                )}

                <Typography sx={{ ...cueLabelSx(tokens), mb: 0.75 }}>Čo má asistent vybaviť</Typography>
                <InputBase
                    multiline
                    fullWidth
                    minRows={3}
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    placeholder="Zisti, či mu vyhovuje zajtrajšia zmena od 6:00, a povedz mu, nech ju potvrdí v aplikácii."
                    sx={{ ...fieldSx, mb: 2 }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        border: `1px solid ${record ? tokens.rec : tokens.hairline}`,
                        px: 1.5,
                        py: 1,
                        mb: 2,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 14, color: tokens.ink }}>Zapísať prepis hovoru</Typography>
                        <Typography sx={{ ...metaSx(tokens), mt: 0.25 }}>
                            {record
                                ? 'asistent na to volaného upozorní'
                                : 'uloží sa len to, že hovor prebehol'}
                        </Typography>
                    </Box>
                    <Switch
                        checked={record}
                        onChange={(e) => setRecord(e.target.checked)}
                        sx={{
                            '& .Mui-checked': { color: tokens.rec },
                            '& .Mui-checked + .MuiSwitch-track': { backgroundColor: tokens.rec },
                        }}
                    />
                </Box>

                <Button
                    variant="contained"
                    disableElevation
                    fullWidth
                    onClick={handleCall}
                    disabled={disabled || !ready || calling}
                    sx={{ borderRadius: 0, py: 1 }}
                >
                    {calling ? 'Vytáčam…' : 'Zavolať'}
                </Button>

                {disabled ? (
                    <Typography sx={{ color: tokens.inkMuted, fontSize: 13, mt: 1.5 }}>
                        Linka je vypnutá — volať sa nedá. Zapni ju hore.
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
};

export default AiCallComposer;
